import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from 'FrontRoyalAngular';
import { useTargetBrandConfig } from 'FrontRoyalAngular/useTargetBrandConfig';
import { type CareerProfile } from 'CareerProfiles';
import { Modal } from 'Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { buildAuthHeaders } from 'BackRoyalApi';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';

type ContactFormModalProps = {
    profile: CareerProfile;
    onClose: () => void;
};

type ContactFormModalPresentationProps = {
    displayName: string;
    subject: string;
    replyTo: string;
    messageBody: string;
    sending: boolean;
    sent: boolean;
    canSend: boolean;
    onReplyToChange: (value: string) => void;
    onMessageBodyChange: (value: string) => void;
    onSend: () => void;
    onClose: () => void;
};

export function ContactFormModalPresentation({
    displayName,
    subject,
    replyTo,
    messageBody,
    sending,
    sent,
    canSend,
    onReplyToChange,
    onMessageBodyChange,
    onSend,
    onClose,
}: ContactFormModalPresentationProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.contactForm' });

    if (sent) {
        return (
            <Modal title={t('title', { name: displayName })} onClose={onClose} size="md" titleAlignment="start">
                <div className="flex flex-col items-center justify-center gap-3 px-7.5 pb-10 pt-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white">
                        <FontAwesomeIcon icon={faCheck} className="text-xl" />
                    </div>
                    <span className="text-base font-medium text-black">{t('messageSent')}</span>
                </div>
            </Modal>
        );
    }

    return (
        <Modal title={t('title', { name: displayName })} onClose={onClose} size="md" titleAlignment="start">
            <div className="flex flex-col gap-4 px-7.5 pb-7.5">
                {/* Subject — read-only */}
                <div>
                    <label htmlFor="contact-subject" className="mb-1 block text-sm font-medium text-slate-grey">
                        {t('subject')}
                    </label>
                    <input
                        id="contact-subject"
                        type="text"
                        value={subject}
                        readOnly
                        className="w-full rounded-[8px] bg-slate-grey-50 px-3 py-2.5 text-base text-black"
                    />
                </div>

                {/* Reply To */}
                <div>
                    <label htmlFor="contact-reply-to" className="mb-1 block text-sm font-medium text-slate-grey">
                        {t('replyTo')}
                    </label>
                    <input
                        id="contact-reply-to"
                        type="email"
                        value={replyTo}
                        onChange={e => onReplyToChange(e.target.value)}
                        className="w-full rounded-[8px] bg-slate-grey-50 px-3 py-2.5 text-base text-black outline-none focus:ring-1 focus:ring-primary"
                    />
                </div>

                {/* Message Body */}
                <div>
                    <label htmlFor="contact-message" className="mb-1 block text-sm font-medium text-slate-grey">
                        {t('message')}
                    </label>
                    <textarea
                        id="contact-message"
                        value={messageBody}
                        onChange={e => onMessageBodyChange(e.target.value)}
                        placeholder={t('placeholder')}
                        rows={5}
                        className="w-full resize-none rounded-[8px] bg-slate-grey-50 px-3 py-2.5 text-base text-black outline-none placeholder:text-slate-grey focus:ring-1 focus:ring-primary"
                    />
                </div>

                {/* Send button */}
                <button
                    type="button"
                    onClick={onSend}
                    disabled={!canSend}
                    className="h-12 w-full rounded-[12px] bg-black text-base font-medium text-white disabled:opacity-50"
                >
                    {sending ? t('sending') : t('send')}
                </button>
            </div>
        </Modal>
    );
}

export function ContactFormModal({ profile, onClose }: ContactFormModalProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.contactForm' });
    const currentUser = useCurrentUser();

    const { brandNameShort: brandName } = useTargetBrandConfig();
    const defaultSubject = t('defaultSubject', { brand: brandName, name: currentUser?.name ?? '' });
    const defaultReplyTo =
        ((currentUser as Record<string, unknown>)?.studentNetworkEmail as string | undefined) ??
        currentUser?.email ??
        '';

    const [subject] = useState(defaultSubject);
    const [replyTo, setReplyTo] = useState(defaultReplyTo);
    const [messageBody, setMessageBody] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const canSend = !!subject && !!replyTo && !!messageBody && !sending;

    const handleSend = useCallback(async () => {
        if (!canSend) return;
        setSending(true);
        try {
            const response = await fetch(`${window.ENDPOINT_ROOT}/api/student_network_messages.json`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...buildAuthHeaders() },
                credentials: 'include',
                body: JSON.stringify({
                    record: {
                        recipient_id: profile.userId,
                        reply_to: replyTo,
                        subject,
                        message_body: messageBody,
                    },
                }),
            });
            if (!response.ok) {
                setSending(false);
                return;
            }
            setSent(true);
            setTimeout(onClose, 1750);
        } catch {
            setSending(false);
        }
    }, [canSend, profile.userId, replyTo, subject, messageBody, onClose]);

    const displayName = profile.name ?? profile.nickname ?? '';

    return (
        <ContactFormModalPresentation
            displayName={displayName}
            subject={subject}
            replyTo={replyTo}
            messageBody={messageBody}
            sending={sending}
            sent={sent}
            canSend={canSend}
            onReplyToChange={setReplyTo}
            onMessageBodyChange={setMessageBody}
            onSend={handleSend}
            onClose={onClose}
        />
    );
}
